<template>
  <div class="employee-pay-plans">
    <!-- Permission denied (403 from any Fair Pay call, ER-12840) -->
    <EXPermissionDeniedState v-if="forbidden && !loading" class="employee-pay-plans__permission" />

    <!-- Error State -->
    <EXErrorState
      v-else-if="error && !loading"
      class="employee-pay-plans__error"
      title="Failed to load pay plans"
      title-class="employee-pay-plans__error-title"
      message-class="employee-pay-plans__error-text"
      retry-class="employee-pay-plans__retry-btn"
      :message="error"
      @retry="loadData"
    />

    <!-- Main Content -->
    <div v-else class="employee-pay-plans__content">
      <div
        v-if="loading"
        class="employee-pay-plans__header-skeleton"
        role="status"
        aria-live="polite"
        data-testid="employee-pay-plans-header-skeleton"
      >
        <div class="employee-pay-plans__skeleton-avatar" />
        <div class="employee-pay-plans__skeleton-copy">
          <div class="employee-pay-plans__skeleton-line employee-pay-plans__skeleton-line--name" />
          <div class="employee-pay-plans__skeleton-line employee-pay-plans__skeleton-line--title" />
          <div class="employee-pay-plans__skeleton-line employee-pay-plans__skeleton-line--badge" />
        </div>
        <div class="employee-pay-plans__skeleton-readiness" />
      </div>
      <EmployeePageHeader
        v-else
        :status="employeeData.status || 'active'"
        :name="employeeFullName"
        :avatar="employeeData.avatar || ''"
        :title="employeeData.title || ''"
        :archived="employeeData.archived || false"
        :tracking="employeeData.tracking !== false"
        :employee-data="employeeData"
        :readiness="readinessData.readiness"
        :last_month_readiness="readinessData.last_month_readiness"
        :breadcrumbs="breadcrumbs"
        :employee-id="resolvedEmployeeId"
        @breadcrumb-click="handleBreadcrumbClick"
      />

      <EmployeePayPlansTimelineAndList
        :assignments="summaryData.assignments || []"
        :pay-plan-versions="summaryData.payPlanVersions || []"
        :today="todayDate"
        :selected-version="selectedVersion"
        :pay-range-versions="payRangeVersionBars"
        :pay-range-versions-selectable="payRangeDrawerEnabled"
        :selected-pay-range-version="selectedPayRangeVersion"
        :loading="loading"
        :bar-navigation="barNavigationEnabled"
        @assignment-select="handleAssignmentSelect"
        @version-select="handleVersionSelect"
        @pay-range-version-select="handlePayRangeVersionSelect"
        @version-hover="handleVersionHover"
        @open-chatter="handleOpenChatter"
        @menu-action="handleMenuAction"
        @create-pay-plan="handleCreatePayPlan"
        @create-proposal="handleCreateProposal"
        @propose-changes="handleCreateProposal"
      />
    </div>

    <!-- First Pay Plan Wizard (ER-11961 / ER-10995) — creates a Proposed version -->
    <div v-if="wizard.type" class="employee-pay-plans__overlay" @click.self="closeWizard">
      <FirstPayPlanWizard
        v-if="wizard.type === 'contract'"
        :step="wizard.step"
        :effective-date="wizard.effectiveDate"
        :effective-by="wizard.effectiveBy"
        :country="wizard.country"
        :currency="wizard.currency"
        :base-salary="wizard.baseSalary"
        :pay-basis="wizard.payBasis"
        :pay-basis-label="wizard.payBasisLabel"
        :calculator-mode="wizard.calculatorMode"
        :pay-range="wizard.payRange"
        :readiness-module-enabled="wizard.readinessModuleEnabled"
        :disabled-ranges="wizard.disabledRanges"
        :next-conflict-date="wizard.nextConflictDate"
        :effective-to-enabled="wizard.effectiveToEnabled"
        :submitting="wizard.submitting"
        @update:effectiveDate="wizard.effectiveDate = $event"
        @update:effectiveBy="wizard.effectiveBy = $event"
        @update:baseSalary="wizard.baseSalary = $event"
        @update:country="wizard.country = $event"
        @update:currency="wizard.currency = $event"
        @update:payBasis="wizard.payBasis = $event"
        @update:payBasisLabel="wizard.payBasisLabel = $event"
        @next="goNext"
        @back="goBack"
        @create-pay-plan="submitContract"
        @close="closeWizard"
      />
      <AddFirstFairPayPlanProposalWizard
        v-else
        :step="wizard.step"
        :effective-date="wizard.effectiveDate"
        :country="wizard.country"
        :currency="wizard.currency"
        :base-salary="wizard.baseSalary"
        :pay-basis="wizard.payBasis"
        :pay-basis-label="wizard.payBasisLabel"
        :calculator-mode="wizard.calculatorMode"
        :pay-range="wizard.payRange"
        :approvers="wizard.approvers"
        :approver-items="wizard.approverItems"
        :approver-loading="wizard.approverLoading"
        :approver-has-more="wizard.approverHasMore"
        :submitting="wizard.submitting"
        @update:effectiveDate="wizard.effectiveDate = $event"
        @update:baseSalary="wizard.baseSalary = $event"
        @update:country="wizard.country = $event"
        @update:currency="wizard.currency = $event"
        @update:payBasis="wizard.payBasis = $event"
        @update:payBasisLabel="wizard.payBasisLabel = $event"
        @add-approver="addApprover"
        @remove-approver="removeApprover"
        @search-approvers="searchApprovers"
        @load-more-approvers="loadMoreApprovers"
        @next="goNext"
        @back="goBack"
        @save-as-draft="handleSaveProposalDraft"
        @close="closeWizard"
      />
    </div>

    <!-- Proposal drawer — Edit row action (ER-12116 / ER-12109) -->
    <ProposedPayPlanDrawerConnected ref="proposalDrawer" :proxy="vm" :fetch-rows="loadData" />

    <!-- Lapsed drawer — committed versions whose period ended (ER-12113) -->
    <LapsedPayPlanDrawerConnected
      ref="lapsedDrawer"
      :proxy="vm"
      :fetch-rows="loadData"
      @open-chatter="handleOpenChatter"
    />

    <!-- Current drawer — the In Force version covering today (ER-12118) -->
    <CurrentPayPlanDrawerConnected
      ref="currentDrawer"
      :proxy="vm"
      :fetch-rows="loadData"
      @open-chatter="handleOpenChatter"
      @propose-changes="handleCreateProposal"
    />

    <!-- Version pane — Future / Voided versions, read-only (ER-12119) -->
    <PayPlanVersionPaneConnected
      ref="versionPane"
      :proxy="vm"
      :fetch-rows="loadData"
      @open-chatter="handleOpenChatter"
      @propose-changes="handleCreateProposal"
    />

    <!-- Pay range version drawer — read-only, opened from a version bar (ER-12117) -->
    <PayRangeVersionDrawerConnected
      ref="payRangeDrawer"
      :feature-enabled="payRangeDrawerEnabled"
      @open-chatter="handleOpenRangeChatter"
      @closed="selectedPayRangeVersion = null"
    />

    <!-- Void reason dialog (Rule B-14: the reason gates confirm) -->
    <div v-if="voidDialog.open" class="employee-pay-plans__overlay" @click.self="closeVoidDialog">
      <div
        class="employee-pay-plans__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="employee-pay-plans-void-title"
      >
        <h3 id="employee-pay-plans-void-title" class="employee-pay-plans__dialog-title">
          Void pay plan version
        </h3>
        <label class="employee-pay-plans__dialog-label" for="employee-pay-plans-void-reason">
          Reason
        </label>
        <textarea
          id="employee-pay-plans-void-reason"
          v-model="voidDialog.reason"
          class="employee-pay-plans__dialog-input"
          data-testid="void-reason"
          rows="3"
        />
        <div class="employee-pay-plans__dialog-actions">
          <button
            type="button"
            class="employee-pay-plans__dialog-cancel"
            data-testid="void-cancel"
            @click="closeVoidDialog"
          >
            Cancel
          </button>
          <button
            type="button"
            class="employee-pay-plans__retry-btn"
            data-testid="void-confirm"
            :disabled="!voidDialog.reason.trim() || voidDialog.submitting"
            @click="confirmVoid"
          >
            Void
          </button>
        </div>
      </div>
    </div>

    <!-- Terminate dialog (Rule A-7: the reason gates confirm, the date is optional) -->
    <div
      v-if="terminateDialog.open"
      class="employee-pay-plans__overlay"
      @click.self="closeTerminateDialog"
    >
      <div
        class="employee-pay-plans__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="employee-pay-plans-terminate-title"
      >
        <h3 id="employee-pay-plans-terminate-title" class="employee-pay-plans__dialog-title">
          Terminate pay plan
        </h3>
        <p class="employee-pay-plans__dialog-text">
          Terminating <strong>{{ terminateDialog.versionCode || "this version" }}</strong> ends the
          plan in force. This cannot be undone.
        </p>
        <label class="employee-pay-plans__dialog-label" for="employee-pay-plans-terminate-date">
          Termination date (optional)
        </label>
        <input
          id="employee-pay-plans-terminate-date"
          v-model="terminateDialog.date"
          type="date"
          class="employee-pay-plans__dialog-input"
          data-testid="terminate-date"
        />
        <label class="employee-pay-plans__dialog-label" for="employee-pay-plans-terminate-reason">
          Reason
        </label>
        <textarea
          id="employee-pay-plans-terminate-reason"
          v-model="terminateDialog.reason"
          class="employee-pay-plans__dialog-input"
          data-testid="terminate-reason"
          rows="3"
        />
        <div class="employee-pay-plans__dialog-actions">
          <button
            type="button"
            class="employee-pay-plans__dialog-cancel"
            data-testid="terminate-cancel"
            :disabled="terminateDialog.submitting"
            @click="closeTerminateDialog"
          >
            Cancel
          </button>
          <button
            type="button"
            class="employee-pay-plans__retry-btn"
            data-testid="terminate-confirm"
            :disabled="!terminateDialog.reason.trim() || terminateDialog.submitting"
            @click="confirmTerminate"
          >
            Terminate
          </button>
        </div>
      </div>
    </div>

    <!-- Chatter Drawer for Version Chatter (shared pane, ER-12069) -->
    <ChecklistDrawer
      v-if="chatterDrawer.show"
      :drawer="chatterDrawer.show"
      :type="chatterDrawer.type"
      :typeValue="chatterDrawer.versionCode"
      :object-type="chatterDrawer.table"
      :object-id="chatterDrawer.versionId"
      :chatterList="chatterDrawer.items"
      :totalCheckpointChatter="chatterDrawer.total"
      :loading="chatterDrawer.loading"
      @closeDrawer="closeChatterDrawer"
      @requestMoreCheckpointsChatter="loadMoreChatter"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import EmployeePageHeader from "@components/EmployeePageHeader/EmployeePageHeader.vue";
import EmployeePayPlansTimelineAndList from "@components/EmployeePayPlansTimelineAndList/EmployeePayPlansTimelineAndList.vue";
import EXErrorState from "@components/EXErrorState/EXErrorState.vue";
import EXPermissionDeniedState from "@components/EXPermissionDeniedState/EXPermissionDeniedState.vue";
import FirstPayPlanWizard from "@components/FirstPayPlanWizard/FirstPayPlanWizard.vue";
import AddFirstFairPayPlanProposalWizard from "@components/AddFirstFairPayPlanProposalWizard/AddFirstFairPayPlanProposalWizard.vue";
import ChecklistDrawer from "@components/Drawer/ChatterDrawer.vue";
import ProposedPayPlanDrawerConnected from "@components/ProposedPayPlanDrawer/ProposedPayPlanDrawerConnected.vue";
import LapsedPayPlanDrawerConnected from "@components/LapsedPayPlanDrawer/LapsedPayPlanDrawerConnected.vue";
import CurrentPayPlanDrawerConnected from "@components/CurrentPayPlanDrawer/CurrentPayPlanDrawerConnected.vue";
import PayPlanVersionPaneConnected from "@components/PayPlanVersionPane/PayPlanVersionPaneConnected.vue";
import PayRangeVersionDrawerConnected from "@components/PayRangeVersionDrawer/PayRangeVersionDrawerConnected.vue";
import {
  computePayPlanPeriodState,
  findPayRangeVersion,
  mapPayRangeVersionsToTimelineBars,
  normalizePayPlanVersion,
  resolveAssignmentPositionId,
} from "@components/EmployeePayPlansTimelineAndList/employeePayPlansTimelineUtils";
import { LD_FLAG_IDEA_MDP_429 } from "@components/constants/launchDarklyFlagKeys";
import {
  deletePayPlanProposal,
  getEmployeePayPlanSummary,
  resetPayPlanProposalToDraft,
  submitPayPlanProposal,
  terminatePayPlanVersionV2,
  voidPayPlanProposal,
  voidPayPlanVersionV2,
} from "@/api/employeeAPI";
import { apiErrorMessage } from "@components/ProposedPayPlanDrawer/composables/useProposalDrawer";
import { usePayPlanWizards } from "@components/PayPlansListPage/composables/usePayPlanWizards";
import { getChatterList } from "@/api";
import { FAIR_PAY_TAB, fairPayListRoute } from "@components/FairPayMenu/fairPayMenuConstants";

/**
 * Chatter table for a pay plan version (one thread per version). Owned by the
 * version-chatter ticket ER-12069, mirroring the singular table names the other
 * chatter callers pass ("employee", "position", "role", "checkpoint").
 */
const PAY_PLAN_VERSION_CHATTER_TABLE = "pay_plan_version";
const PAY_RANGE_VERSION_CHATTER_TABLE = "pay_range_version";

const props = defineProps({
  employeeId: {
    type: [String, Number],
    default: null,
  },
});

const instance = getCurrentInstance();
const vm = instance?.proxy || {};

// Reactive state
const loading = ref(true);
const error = ref(null);
/** ER-12840 — a 403 from any Fair Pay call is a permission state, not an error toast. */
const forbidden = ref(false);
const employeeData = ref({});
const summaryData = ref({
  assignments: [],
  subscriptions: [],
  payPlanVersions: [],
});
const selectedVersion = ref(null);
const selectedPayRangeVersion = ref(null);
const payRangeDrawerEnabled = ref(false);
/** ER-12449 — Occupies Positions bars navigate; flag-gated (idea-MDP-429). */
const barNavigationEnabled = ref(false);
const readinessData = ref({
  readiness: null,
  last_month_readiness: null,
});

// Chatter drawer state
const chatterDrawer = ref({
  show: false,
  versionId: null,
  versionCode: "",
  table: PAY_PLAN_VERSION_CHATTER_TABLE,
  type: "Pay Plan Version",
  items: [],
  total: 0,
  page: 1,
  loading: false,
});

const proposalDrawer = ref(null);
const lapsedDrawer = ref(null);
const currentDrawer = ref(null);
const versionPane = ref(null);
const payRangeDrawer = ref(null);
const voidDialog = ref({
  open: false,
  versionId: null,
  inForce: false,
  reason: "",
  submitting: false,
});
const terminateDialog = ref({
  open: false,
  versionId: null,
  versionCode: "",
  date: "",
  reason: "",
  submitting: false,
});

// Pay Plan Wizard integration
const {
  wizard,
  openAddPlan,
  openProposePlan,
  close: closeWizard,
  goNext,
  goBack,
  addApprover,
  removeApprover,
  searchApprovers,
  loadMoreApprovers,
  submitContract,
  submitProposal,
} = usePayPlanWizards({
  fetchRows: loadData,
  proxy: vm,
});

// Computed properties
const resolvedEmployeeId = computed(
  () => props.employeeId || vm.$route?.params?.employeeId || vm.$route?.params?.id
);

const employeeFullName = computed(() => {
  const first = employeeData.value.first_name || "";
  const last = employeeData.value.last_name || "";
  return `${first} ${last}`.trim();
});

const todayDate = computed(() => {
  return new Date().toISOString().split("T")[0];
});

const payRangeVersionBars = computed(() =>
  mapPayRangeVersionsToTimelineBars(summaryData.value.subscriptions, todayDate.value)
);

const breadcrumbs = computed(() => [
  { text: "Pay Plans", route: fairPayListRoute(FAIR_PAY_TAB.PAY_PLANS) },
  { text: "Employee Pay Plans", disabled: true },
]);

// Helper functions
function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

// Data loading
async function loadData() {
  const accountId = getAccountId();
  const employeeId = resolvedEmployeeId.value;

  if (!accountId || !employeeId) {
    error.value = "Missing account or employee ID";
    loading.value = false;
    return;
  }

  loading.value = true;
  error.value = null;
  forbidden.value = false;

  try {
    const response = await getEmployeePayPlanSummary(accountId, employeeId);
    const data = response?.data || {};
    const employee = data.employee || {};

    // ER-12668 — the header reads the employee straight off pay-plan-summary.
    // No placeholder name/title/readiness: an absent field renders empty.
    employeeData.value = {
      id: employee.id ?? employeeId,
      first_name: employee.first_name || "",
      last_name: employee.last_name || "",
      avatar: employee.avatar || "",
      title: employee.title || "",
      status: employee.status || "active",
      archived: employee.archived || false,
      tracking: employee.tracking !== false,
    };

    // Extract readiness data
    readinessData.value = {
      readiness: employee.readiness ?? null,
      last_month_readiness: employee.last_month_readiness ?? null,
    };

    // Extract summary data
    summaryData.value = {
      assignments: data.assignments || [],
      subscriptions: data.subscriptions || [],
      payPlanVersions: (data.pay_plan_versions || []).map(normalizePayPlanVersion),
    };
  } catch (err) {
    // ER-12840 — a denied user sees the permission-denied state, never a toast.
    if (err?.response?.status === 403) {
      forbidden.value = true;
    } else {
      error.value = err?.response?.data?.detail || err?.message || "An error occurred";
    }
  } finally {
    loading.value = false;
  }
}

// Event handlers
function handleBreadcrumbClick(breadcrumb) {
  if (breadcrumb?.disabled) return;

  if (breadcrumb.route) {
    vm.$router?.push(breadcrumb.route).catch(() => {});
  } else if (breadcrumb.href) {
    vm.$router?.push(breadcrumb.href).catch(() => {});
  }
}

/** Find a summary version by id (bar clicks only pass the id). */
function findSummaryVersion(versionId) {
  return (
    (summaryData.value.payPlanVersions || []).find(
      (item) => String(item.id) === String(versionId)
    ) || null
  );
}

/** Employee identity for the drawer header (not part of the version detail). */
function drawerEmployeeIdentity() {
  const first = employeeData.value.first_name || "";
  const last = employeeData.value.last_name || "";
  return {
    name: `${first} ${last}`.trim(),
    title: employeeData.value.title || "",
    avatarUrl: employeeData.value.avatar || null,
  };
}

/**
 * Single drawer-routing table for the page (ER-12065). A row or bar click opens
 * exactly one drawer, chosen from the version's lifecycle + period state:
 * Proposed -> ProposedPayPlanDrawer, In Force/Current -> CurrentPayPlanDrawer,
 * In Force/Lapsed or Terminated -> LapsedPayPlanDrawer.
 */
function handleVersionSelect(version) {
  // Handle both object (from row click) and id (from bar click)
  const versionId = typeof version === "object" ? version?.id : version;
  selectedVersion.value = versionId;
  closePayRangeDrawer();

  const resolved = version && typeof version === "object" ? version : findSummaryVersion(versionId);
  if (!resolved) return;

  const accountId = getAccountId();
  const employeeId = resolvedEmployeeId.value;
  const periodState = computePayPlanPeriodState(resolved, todayDate.value);

  // Proposed: open the proposal drawer (ER-12116 routing table row 1).
  if (resolved.status === "Proposed") {
    proposalDrawer.value?.open({
      accountId,
      employeeId,
      proposalId: resolved.id,
      approvalStatus: resolved.approvalStatus || null,
    });
    return;
  }

  // Lapsed: In Force with a closed effective_to, or Terminated (ER-12113).
  if (
    resolved.status === "Terminated" ||
    (resolved.status === "In Force" && periodState === "Lapsed")
  ) {
    lapsedDrawer.value?.open({ accountId, employeeId, versionId: resolved.id });
    return;
  }

  // Voided: read-only pane, no actions (ER-12119).
  if (resolved.status === "Voided") {
    versionPane.value?.open({
      accountId,
      employeeId,
      versionId: resolved.id,
      employee: drawerEmployeeIdentity(),
    });
    return;
  }

  // Future: an In Force version whose period has not started (ER-12119).
  if (resolved.status === "In Force" && periodState === "Future") {
    versionPane.value?.open({
      accountId,
      employeeId,
      versionId: resolved.id,
      employee: drawerEmployeeIdentity(),
    });
    return;
  }

  // Current: In Force and covering today (ER-12118).
  if (resolved.status === "In Force" && periodState === "Current") {
    currentDrawer.value?.open({
      accountId,
      employeeId,
      versionId: resolved.id,
      employee: drawerEmployeeIdentity(),
      // B-18 note: name when the scheduled change starts (the detail carries the
      // code but not the date).
      futurePlanDate: scheduledVersionDate(),
    });
  }
}

/** Effective-from of the employee's scheduled (In Force + Future) version. */
function scheduledVersionDate() {
  const future = (summaryData.value.payPlanVersions || []).find(
    (item) =>
      item.status === "In Force" && computePayPlanPeriodState(item, todayDate.value) === "Future"
  );
  return future?.effectiveFrom || null;
}

function closePayRangeDrawer() {
  payRangeDrawer.value?.close();
  selectedPayRangeVersion.value = null;
}

function closePayPlanDrawers() {
  proposalDrawer.value?.close();
  lapsedDrawer.value?.close();
  currentDrawer.value?.close();
  versionPane.value?.close();
}

/**
 * Pay range version bar click (ER-12117). The summary already carries everything
 * the drawer shows, so nothing is fetched; Voided versions never reach here.
 */
function handlePayRangeVersionSelect(versionId) {
  if (!payRangeDrawerEnabled.value) return;
  const match = findPayRangeVersion(summaryData.value.subscriptions, versionId);
  if (!match) return;

  closePayPlanDrawers();
  selectedVersion.value = null;
  selectedPayRangeVersion.value = match.version.id;
  payRangeDrawer.value?.open({
    version: match.version,
    subscription: match.subscription,
    employee: drawerEmployeeIdentity(),
    today: todayDate.value,
  });
}

/** Occupies Positions bar -> Position detail (explicit route, so Back returns here). */
function handleAssignmentSelect(assignment) {
  const positionId = resolveAssignmentPositionId(assignment);
  if (positionId === null) return;
  vm.$router
    ?.push({ name: "FairPayPosition", params: { positionId: String(positionId) } })
    .catch(() => {});
}

async function loadBarNavigationFlag() {
  try {
    const enabled = (await vm.$getFlag?.(LD_FLAG_IDEA_MDP_429, false)) === true;
    barNavigationEnabled.value = enabled;
    payRangeDrawerEnabled.value = enabled;
  } catch {
    barNavigationEnabled.value = false;
    payRangeDrawerEnabled.value = false;
  }
}

function handleVersionHover() {
  // Optional: could highlight the version or show a tooltip — the timeline owns the
  // hover styling today, so the handler only keeps the list's event wired up.
}

function handleOpenChatter(version) {
  // R8: open this pay plan version's chatter in the shared chatter pane (ER-12069)
  return openChatter({
    versionId: version?.id || version,
    versionCode: version?.versionCode,
    table: PAY_PLAN_VERSION_CHATTER_TABLE,
    type: "Pay Plan Version",
  });
}

function handleOpenRangeChatter(version) {
  // The pay_range_version thread, through the same shared pane (ER-12117)
  return openChatter({
    versionId: version?.id,
    versionCode: version?.versionCode,
    table: PAY_RANGE_VERSION_CHATTER_TABLE,
    type: "Pay Range Version",
  });
}

async function openChatter({ versionId, versionCode, table, type }) {
  const accountId = getAccountId();

  if (!accountId || !versionId) return;

  chatterDrawer.value = {
    show: true,
    versionId,
    versionCode: versionCode || String(versionId),
    table,
    type,
    items: [],
    total: 0,
    page: 1,
    loading: true,
  };

  try {
    const response = await getChatterList(accountId, versionId, table, 1);

    chatterDrawer.value.items = response?.data?.results || [];
    chatterDrawer.value.total = response?.data?.count || 0;
  } catch (err) {
    if (vm.$notifications?.error) {
      vm.$notifications.error("Could not load chatter");
    }
  } finally {
    chatterDrawer.value.loading = false;
  }
}

function closeChatterDrawer() {
  chatterDrawer.value.show = false;
}

async function loadMoreChatter() {
  if (
    chatterDrawer.value.loading ||
    chatterDrawer.value.items.length >= chatterDrawer.value.total
  ) {
    return;
  }

  const accountId = getAccountId();
  chatterDrawer.value.page += 1;
  chatterDrawer.value.loading = true;

  try {
    const response = await getChatterList(
      accountId,
      chatterDrawer.value.versionId,
      chatterDrawer.value.table,
      chatterDrawer.value.page
    );

    chatterDrawer.value.items = [...chatterDrawer.value.items, ...(response?.data?.results || [])];
    chatterDrawer.value.total = response?.data?.count || 0;
  } catch (err) {
    if (vm.$notifications?.error) {
      vm.$notifications.error("Could not load more chatter");
    }
  } finally {
    chatterDrawer.value.loading = false;
  }
}

function closeVoidDialog() {
  voidDialog.value = {
    open: false,
    versionId: null,
    inForce: false,
    reason: "",
    submitting: false,
  };
}

/** Backend `detail` when the API answered with one, otherwise the generic text. */
function errorMessage(err, fallback) {
  return err?.response?.data?.detail || fallback;
}

async function confirmVoid() {
  const accountId = getAccountId();
  const { versionId, inForce, reason } = voidDialog.value;
  if (!accountId || !versionId || !reason.trim()) return;

  voidDialog.value.submitting = true;
  try {
    // A committed (In Force) version is voided on the version viewset; only a
    // ruled-on proposal goes through the proposal viewset (Rules A-8/A-9, B-19).
    const voidVersion = inForce ? voidPayPlanVersionV2 : voidPayPlanProposal;
    await voidVersion(accountId, versionId, { reason: reason.trim() });
    closeVoidDialog();
    if (vm.$notifications?.success) {
      vm.$notifications.success("Pay plan version voided");
    }
    await loadData();
  } catch (err) {
    voidDialog.value.submitting = false;
    if (vm.$notifications?.error) {
      vm.$notifications.error(errorMessage(err, "Could not void pay plan version"));
    }
  }
}

function closeTerminateDialog() {
  if (terminateDialog.value.submitting) return;
  terminateDialog.value = {
    open: false,
    versionId: null,
    versionCode: "",
    date: "",
    reason: "",
    submitting: false,
  };
}

async function confirmTerminate() {
  const accountId = getAccountId();
  const { versionId, date, reason } = terminateDialog.value;
  if (!accountId || !versionId || !reason.trim()) return;

  terminateDialog.value.submitting = true;
  try {
    await terminatePayPlanVersionV2(accountId, versionId, {
      termination_date: date || null,
      reason: reason.trim(),
    });
    terminateDialog.value.submitting = false;
    closeTerminateDialog();
    if (vm.$notifications?.success) {
      vm.$notifications.success("Pay plan version terminated");
    }
    await loadData();
  } catch (err) {
    terminateDialog.value.submitting = false;
    if (vm.$notifications?.error) {
      vm.$notifications.error(apiErrorMessage(err, "Could not terminate pay plan version"));
    }
  }
}

async function handleMenuAction(version, action) {
  // R3: run the chosen row action (Rule B-19) on this version, then re-fetch.
  // Actions: edit, submit, delete, reset_to_draft, void, terminate
  if (!action || !version) return;

  const versionId = version?.id || version;
  const accountId = getAccountId();
  if (!accountId || !versionId) return;

  if (action === "edit") {
    closePayRangeDrawer();
    // The drawer re-fetches the page itself after each of its own actions.
    // Pass the row's approval lifecycle so the drawer hides draft-only actions
    // (Add Approver / Submit / editable fields) once the proposal moved on.
    proposalDrawer.value?.open({
      employeeId: resolvedEmployeeId.value,
      proposalId: versionId,
      approvalStatus: version?.approvalStatus || null,
    });
    return;
  }

  if (action === "void") {
    voidDialog.value = {
      open: true,
      versionId,
      inForce: version?.status === "In Force",
      reason: "",
      submitting: false,
    };
    return;
  }

  if (action === "terminate") {
    terminateDialog.value = {
      open: true,
      versionId,
      versionCode: version?.versionCode || "",
      date: "",
      reason: "",
      submitting: false,
    };
    return;
  }

  if (action === "delete") {
    const confirmed = vm.$confirm
      ? await vm.$confirm("Are you sure you want to delete this pay plan version?")
      : true;

    if (!confirmed) return;
  }

  try {
    if (action === "submit") {
      await submitPayPlanProposal(accountId, versionId);
    } else if (action === "delete") {
      await deletePayPlanProposal(accountId, versionId);
    } else if (action === "reset_to_draft") {
      await resetPayPlanProposalToDraft(accountId, versionId);
    } else {
      return;
    }

    if (vm.$notifications?.success) {
      const actionPast = {
        submit: "submitted for approval",
        delete: "deleted",
        reset_to_draft: "reset to draft",
      };
      vm.$notifications.success(`Pay plan version ${actionPast[action]}`);
    }

    // Re-fetch data after action completes
    await loadData();
  } catch (err) {
    if (vm.$notifications?.error) {
      vm.$notifications.error(errorMessage(err, `Could not ${action} pay plan version`));
    }
  }
}

function handleCreatePayPlan() {
  // Decision 2: R4 - Open first-pay-plan wizard to create Proposed version
  const employeeRow = {
    employee: {
      id: resolvedEmployeeId.value,
      first_name: employeeData.value.first_name,
      last_name: employeeData.value.last_name,
    },
    id: resolvedEmployeeId.value,
    positionTitle: employeeData.value.title,
  };
  openAddPlan(employeeRow);
}

function handleCreateProposal() {
  // Decision 2: R4 - Open wizard for creating proposal to cover coverage gap.
  // Bound to both events: EmployeePayPlansTimelineAndList declares "create-proposal"
  // (coverage-gap entry point) but its propose-changes button emits "propose-changes",
  // so the same create-proposal flow answers both.
  const employeeRow = {
    employee: {
      id: resolvedEmployeeId.value,
      first_name: employeeData.value.first_name,
      last_name: employeeData.value.last_name,
    },
    id: resolvedEmployeeId.value,
    positionTitle: employeeData.value.title,
  };
  openProposePlan(employeeRow);
}

/**
 * Propose Changes → wizard → Save as draft: create the Draft, then open the
 * proposal drawer on it (ER-12109 / ER-12116).
 */
async function handleSaveProposalDraft() {
  const proposal = await submitProposal();
  if (!proposal) return;
  closePayRangeDrawer();
  proposalDrawer.value?.open({
    employeeId: resolvedEmployeeId.value,
    proposalId: proposal.id,
    approvalStatus: proposal.approval_status || "Draft",
  });
}

// Lifecycle
onMounted(async () => {
  loadData();
  loadBarNavigationFlag();
});
</script>

<style scoped>
.employee-pay-plans {
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  padding: 16px;
}

.employee-pay-plans__content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.employee-pay-plans__overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 48px 16px;
  background: rgba(16, 24, 40, 0.45);
  overflow-y: auto;
}

.employee-pay-plans__dialog {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 480px;
  height: fit-content;
  padding: 24px;
  background: #ffffff;
  border-radius: 12px;
}

.employee-pay-plans__dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #101828;
}

.employee-pay-plans__dialog-text {
  margin: 0;
  font-size: 14px;
  color: #344054;
}

.employee-pay-plans__dialog-label {
  font-size: 14px;
  font-weight: 500;
  color: #344054;
}

.employee-pay-plans__dialog-input {
  padding: 8px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 14px;
}

.employee-pay-plans__dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.employee-pay-plans__dialog-cancel {
  padding: 8px 16px;
  background: #ffffff;
  color: #344054;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.employee-pay-plans__permission,
.employee-pay-plans__error {
  min-height: 400px;
}

.employee-pay-plans__header-skeleton {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 0 16px;
  border-bottom: 1px solid #eaecf0;
}

.employee-pay-plans__skeleton-avatar {
  flex: 0 0 auto;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #f2f4f7;
  animation: employee-pay-plans-pulse 1.4s ease-in-out infinite;
}

.employee-pay-plans__skeleton-copy {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.employee-pay-plans__skeleton-line,
.employee-pay-plans__skeleton-readiness {
  border-radius: 6px;
  background: #f2f4f7;
  animation: employee-pay-plans-pulse 1.4s ease-in-out infinite;
}

.employee-pay-plans__skeleton-line--name {
  width: 180px;
  height: 20px;
}

.employee-pay-plans__skeleton-line--title {
  width: 120px;
  height: 14px;
}

.employee-pay-plans__skeleton-line--badge {
  width: 72px;
  height: 20px;
  border-radius: 1000px;
}

.employee-pay-plans__skeleton-readiness {
  flex: 0 0 auto;
  width: 200px;
  height: 40px;
}

@keyframes employee-pay-plans-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}

.employee-pay-plans__retry-btn {
  padding: 8px 16px;
  background: #7f56d9;
  color: #ffffff;
  border: 1px solid #7f56d9;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.employee-pay-plans__retry-btn:hover {
  background: #6941c6;
  border-color: #6941c6;
}
</style>
